import { Household, InvitationCreateRequest, HouseholdCreateRequest } from '../types/household';
import { apiRequest } from './apiClient';

export const fetchUserHouseholds = async (): Promise<Household[]> => {
  try {
    const response = await apiRequest.get('api/households/user', {
      requiresHouseholdId: false,
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized - please log in again');
      } else if (response.status >= 500) {
        throw new Error('Server error - please try again later');
      } else {
        throw new Error('Failed to fetch households');
      }
    }

    return response.json();
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('Cannot connect to server');
    }
    throw error;
  }
};

export const createHouseholdInvitation = async (
  householdId: string,
  data: InvitationCreateRequest
): Promise<{ message: string; id: string }> => {
  try {
    const response = await apiRequest.post(
      `api/households/${householdId}/invitations`,
      data,
      {
        requiresHouseholdId: false,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 403) {
        throw new Error('Access Denied. You are not the owner of this household.');
      } else if (response.status === 422) {
        throw new Error(errorData.message || 'Validation error');
      } else if (response.status >= 500) {
        throw new Error('Server error - please try again later');
      } else {
        throw new Error(errorData.message || 'Failed to create invitation');
      }
    }

    return response.json();
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('Cannot connect to server');
    }
    throw error;
  }
};

export const acceptHouseholdInvitation = async (invitationId: string): Promise<void> => {
  const apiUrl = process.env.REACT_APP_API_URL;

  try {
    const response = await fetch(`${apiUrl}api/invitations/accept/${invitationId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 400) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Invalid invitation');
      } else if (response.status >= 500) {
        throw new Error('Server error - please try again later');
      } else {
        throw new Error('Failed to accept invitation');
      }
    }
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('Cannot connect to server');
    }
    throw error;
  }
};

export const createHousehold = async (
  data: HouseholdCreateRequest
): Promise<{ message: string; id: string }> => {
  try {
    const response = await apiRequest.post(
      'api/households',
      data,
      {
        requiresHouseholdId: false,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 422) {
        throw new Error(errorData.message || 'Validation error');
      } else if (response.status >= 500) {
        throw new Error('Server error - please try again later');
      } else {
        throw new Error(errorData.message || 'Failed to create household');
      }
    }

    return response.json();
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('Cannot connect to server');
    }
    throw error;
  }
};

export const leaveHousehold = async (householdId: string): Promise<void> => {
  try {
    const response = await apiRequest.patch(
      `api/households/${householdId}/leave`,
      {},
      {
        requiresHouseholdId: false,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 403) {
        throw new Error('Access Denied. You are not a member of this household.');
      } else if (response.status === 404) {
        throw new Error('Household not found.');
      } else if (response.status >= 500) {
        throw new Error('Server error - please try again later');
      } else {
        throw new Error(errorData.message || 'Failed to leave household');
      }
    }
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('Cannot connect to server');
    }
    throw error;
  }
};

export const deleteMemberFromHousehold = async (
  householdId: string,
  memberId: string
): Promise<void> => {
  try {
    const response = await apiRequest.delete(
      `api/households/${householdId}/delete-member/${memberId}`,
      {
        requiresHouseholdId: false,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 403) {
        throw new Error('Access Denied. You are not the owner of this household.');
      } else if (response.status === 404) {
        throw new Error('Household or member not found.');
      } else if (response.status >= 500) {
        throw new Error('Server error - please try again later');
      } else {
        throw new Error(errorData.message || 'Failed to remove member');
      }
    }
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('Cannot connect to server');
    }
    throw error;
  }
};

export const deleteHousehold = async (householdId: string): Promise<void> => {
  try {
    const response = await apiRequest.delete(
      `api/households/${householdId}`,
      {
        requiresHouseholdId: false,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 403) {
        throw new Error('Access Denied. You are not the owner of this household.');
      } else if (response.status === 404) {
        throw new Error('Household not found.');
      } else if (response.status >= 500) {
        throw new Error('Server error - please try again later');
      } else {
        throw new Error(errorData.message || 'Failed to delete household');
      }
    }
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('Cannot connect to server');
    }
    throw error;
  }
};

export const updateHousehold = async (
  householdId: string,
  data: { name: string }
): Promise<void> => {
  try {
    const response = await apiRequest.patch(
      `api/households/${householdId}`,
      data,
      {
        requiresHouseholdId: false,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 403) {
        throw new Error('Access Denied. You are not the owner of this household.');
      } else if (response.status === 404) {
        throw new Error('Household not found.');
      } else if (response.status === 422) {
        throw new Error(errorData.message || 'Validation error');
      } else if (response.status >= 500) {
        throw new Error('Server error - please try again later');
      } else {
        throw new Error(errorData.message || 'Failed to update household');
      }
    }
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('Cannot connect to server');
    }
    throw error;
  }
};